'use client';

/**
 * GlassTooltip Component
 *
 * A tooltip with glassmorphism styling and smooth animations.
 * Automatically adapts to light/dark theme.
 */

import { alpha, styled, useTheme } from '@mui/material/styles';
import Tooltip, { type TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import React from 'react';

// ============================================================================
// Types
// ============================================================================

export interface GlassTooltipProps extends TooltipProps {
  /**
   * Enable glassmorphism blur effect
   * @default true
   */
  blur?: boolean;

  /**
   * Glow color for the tooltip
   * @default 'none'
   */
  glowColor?: 'none' | 'primary' | 'secondary';
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledTooltipProps {
  $isDark: boolean;
  $blur: boolean;
  $glowColor: 'none' | 'primary' | 'secondary';
}

const StyledTooltip = styled(
  ({ className, children, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }}>
      {/* Wrap children in <span> so MUI v7 Tooltip's data-* attribute
          check always succeeds — React components (GlassIconButton, etc.)
          may not forward data-* attributes to their root DOM node. */}
      <span style={{ display: 'inline-flex' }}>{children}</span>
    </Tooltip>
  ),
  {
    shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
  },
)<StyledTooltipProps>(({ theme, $isDark, $blur, $glowColor }) => {
  const tooltipBg = $isDark ? 'rgba(28,28,30,0.65)' : 'rgba(255,255,255,0.72)';
  const tooltipBorder = $isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.80)';

  return {
    [`& .${tooltipClasses.tooltip}`]: {
      // ── Frosted glass tooltip ──
      background: tooltipBg,
      backdropFilter: $blur ? 'blur(20px) saturate(180%)' : 'none',
      WebkitBackdropFilter: $blur ? 'blur(20px) saturate(180%)' : 'none',
      border: `0.5px solid ${tooltipBorder}`,
      boxShadow: [
        `0 4px 16px rgba(0, 0, 0, ${$isDark ? '0.25' : '0.08'})`,
        `0 1px 4px rgba(0, 0, 0, ${$isDark ? '0.15' : '0.04'})`,
        `inset 0 0.5px 0 rgba(255, 255, 255, ${$isDark ? '0.10' : '0.55'})`,
        $glowColor !== 'none' ? `0 0 12px ${alpha(theme.palette[$glowColor].main, 0.25)}` : '',
      ]
        .filter(Boolean)
        .join(', '),
      borderRadius: 12,
      padding: theme.spacing(1, 1.5),
      color: theme.palette.text.primary,
      fontSize: '0.8125rem',
      fontWeight: 500,
      maxWidth: 300,

      // Arrow styling
      [`& .${tooltipClasses.arrow}`]: {
        color: tooltipBg,
        '&::before': {
          border: `0.5px solid ${tooltipBorder}`,
          backgroundColor: tooltipBg,
        },
      },
    },

    [`& .${tooltipClasses.arrow}`]: {
      color: tooltipBg,
    },
  };
});

// ============================================================================
// Component
// ============================================================================

export const GlassTooltip = React.forwardRef<HTMLDivElement, GlassTooltipProps>(
  ({ blur = true, glowColor = 'none', arrow = true, enterDelay = 200, leaveDelay = 100, ...props }, _ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
      <StyledTooltip
        $isDark={isDark}
        $blur={blur}
        $glowColor={glowColor}
        arrow={arrow}
        enterDelay={enterDelay}
        leaveDelay={leaveDelay}
        {...props}
      />
    );
  },
);

GlassTooltip.displayName = 'GlassTooltip';

export default GlassTooltip;
